import { Card, Space, Typography, theme } from "antd";
import ReactQuill from "react-quill-new";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Question } from "@types";
import { useTranslation } from "react-i18next";
import { HtmlParser } from "@utils";
import { AIGradingPanel } from "./AIGradingPanel";
import { FileCheck } from "lucide-react";

const { useToken } = theme;

interface Props {
  question?: Question;
  /** Callback when AI suggestion should be applied to points */
  onApplyAISuggestion?: (points: number) => void;
}

// EssayOption and ShortAnswer options are the same
export const EssayOption = ({ question, onApplyAISuggestion }: Props) => {
  const [value, setValue] = useState('<p><br></p>');
  const { t } = useTranslation();
  const { token } = useToken();

  // Check if the answer is empty or just whitespace/empty HTML
  const hasNoAnswer = !question?.playerAnswers?.[0]?.value || 
    question.playerAnswers[0].value.replace(/<[^>]*>/g, '').trim() === '';

  useEffect(() => {
    if (question) {
      setValue(question.playerAnswers?.[0]?.value || "<p><br></p>");
    }
  }, [question])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Space direction="vertical" style={{ display: "flex" }}>
        <Typography.Text strong>{t("pages.examPeriods.answer")}:</Typography.Text>
        <ReactQuill readOnly theme="snow" value={value}/>
      </Space>
      
      {/* Expected Answer Section - styled card for better UX */}
      {question?.answers && question.answers.length > 0 && (
        <Card
          size="small"
          style={{ 
            marginTop: 16,
            backgroundColor: token.colorSuccessBg,
            borderColor: token.colorSuccessBorder,
          }}
          title={
            <Space>
              <FileCheck size={16} style={{ color: token.colorSuccess }} />
              <Typography.Text strong style={{ color: token.colorSuccessText }}>
                {t("pages.examPeriods.expectedAnswer")}
              </Typography.Text>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {question.answers.map((answer, index) => (
              <div 
                key={index}
                style={{
                  padding: '8px 12px',
                  backgroundColor: token.colorBgContainer,
                  borderRadius: 6,
                  border: `1px solid ${token.colorSuccessBorderHover}`,
                }}
              >
                <Typography.Text>
                  <HtmlParser htmlString={answer.value}/>
                </Typography.Text>
              </div>
            ))}
          </Space>
        </Card>
      )}

      {/* AI Grading Panel - only shown for essay questions during correction */}
      <AIGradingPanel 
        examInstanceQuestionId={question?.id}
        maxPoints={question?.correctAnswerPoints}
        hasNoAnswer={hasNoAnswer}
        onApplySuggestion={onApplyAISuggestion}
      />
    </motion.div>
  );
};
